import GIF from '../../utils/gif.js';
import { COMMON_TYPES, UI_TYPES } from './types';
import { reducePixels } from './../../utils/reducePixels';
import { loadColors } from './toolsActions';
import { onionSkinLayers, onionSkinNextFrame, onionSkinPreFrame } from './onionskinActions';
import { normalizePixels } from '../../utils/normalizePixels';
import { showDimensionView, hideDimensionView } from './uiActions';
import { requestSave } from './userActions.js'; // eslint-disable-line
import { debugMode } from '../../utils/mocks';

export const redrawAllCanvases = () => (dispatch) => {
  dispatch(updateDrawCanvas());
  dispatch(updateRAWFrameCanvas());
  dispatch(updateRAWCanvas());
  dispatch(updateBgCanvas());
  dispatch(updateAreaCanvas());
};

export const resize = () => (dispatch, getState) => {
  const settings = getState().settings;
  // const s = window.innerHeight - 25 - 350;
  const d = 600; // s <= 700 ? s : 700;
  dispatch({ type: UI_TYPES.SET_CANVAS_PX_SIZE, payload: d });
  const size = settings.canvasPxSize;
  const screen = { width: size, height: size, orgW: window.innerWidth, orgH: window.innerHeight };

  dispatch({
    type: UI_TYPES.SCREEN_RESIZE,
    payload: screen,
  });

  if (window.innerHeight > window.innerWidth) {
    dispatch(showDimensionView());
  } else {
    if (window.innerWidth >= 1300 && window.innerHeight >= 690) {
      dispatch(hideDimensionView());
    } else {
      if (!debugMode) {
        dispatch(showDimensionView());
      } else {
        // console.warn('screen dimension warning disabled in debug mode');
      }
    }
  }

  dispatch(updateBgCanvas());

  settings.drawCanvasRef && dispatch(updateDrawCanvas());
  settings.areaCanvasRef && dispatch(updateAreaCanvas());
};

export const modifyScaleOffset = (value) => (dispatch) => {
  dispatch({ type: UI_TYPES.CANVAS_SCALE, payload: value });
};

export const setCanvasHover = (value) => (dispatch) => dispatch({ type: UI_TYPES.CANVAS_HOVER, payload: value });

export const saveCurrentPixelData = (layers) => (dispatch, getState) => {
  const priorData = JSON.parse(JSON.stringify(getState().data.priorData));
  if (priorData.length < 8) {
    priorData.push(layers);
  } else {
    priorData.shift();
    priorData.push(layers);
  }
  dispatch({ type: COMMON_TYPES.SAVE_CURRENT_DATA, payload: priorData });
};

export const setPixels = (data) => ({
  type: COMMON_TYPES.SET_PIXELS,
  payload: data,
});

export const setToolCanvasRef = (ref) => ({
  type: COMMON_TYPES.SET_TOOL_CANVAS_REF,
  payload: ref,
});

export const setAreaCanvasRef = (ref) => ({
  type: COMMON_TYPES.SET_AREA_CANVAS_REF,
  payload: ref,
});

export const setRawFrameCanvasRef = (ref) => ({
  type: COMMON_TYPES.SET_RAW_FAME_CANVAS_REF,
  payload: ref,
});

export const setSpritesheetCanvasRef = (ref) => ({
  type: COMMON_TYPES.SET_SPRITESHEET_CANVAS_REF,
  payload: ref,
});

export const setRawCanvasRef = (ref) => ({
  type: COMMON_TYPES.SET_RAW_CANVAS_REF,
  payload: ref,
});

export const setDrawCanvasRef = (ref) => (dispatch) => {
  dispatch({ type: COMMON_TYPES.SET_DRAW_CANVAS_REF, payload: ref });
  setTimeout(() => {
    dispatch(updateDrawCanvas());
    dispatch(resize());
  }, 100);
};

export const setBackgroundCanvasRef = (ref) => (dispatch) => {
  dispatch({ type: COMMON_TYPES.SET_BACKGROUND_CANVAS_REF, payload: ref });

  setImmediate(() => {
    dispatch(updateBgCanvas());
  });
};

export const generateSpriteData = (layer = null, frame = null) => (dispatch, getState) =>
  new Promise((resolve) => {
    if (getState().settings.firstPaint === false) {
      dispatch(loadColors([getState().tools.activeColor]));
      dispatch({ type: COMMON_TYPES.FIRST_PAINT });
    }

    dispatch(updateDrawCanvas());
    dispatch(updateRAWFrameCanvas(layer, frame));
    dispatch(updateRAWCanvas(layer, frame));

    const data = dispatch(snapShots());

    dispatch(updateFrameSnapshot(data.rawFrame));
    dispatch(updateLayerSnapshot(data.rawData));

    resolve();
  });

export const updateLayerSnapshot = (data) => ({
  type: COMMON_TYPES.UPDATE_LAYER_SNAPSHOT,
  payload: data,
});

export const updateFrameSnapshot = (data) => ({
  type: COMMON_TYPES.UPDATE_FRAME_SNAPSHOT,
  payload: data,
});

const snapShots = () => (dispatch, getState) => {
  // generate single frame snapshot
  const bRawFrame64 = getState().settings.rawFrameRef.toDataURL('image/png', 0.01);
  // generate layer snapshot
  const bRawData64 = getState().settings.rawCanvasRef.toDataURL('image/png', 0.01);

  const transparent =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

  let rawData;
  let rawFrame;

  const data = getState().data;
  if (data.layers[data.currentLayer] && data.layers[data.currentLayer].pixelData[data.currentFrame].length === 0) {
    rawData = transparent;
    rawFrame = transparent;
  } else {
    rawData = bRawData64;
    rawFrame = bRawFrame64;
  }

  return { rawData, rawFrame };
};

export const getPixelBoundariesOfFrame = (pixels, gridSize) => {
  let minx = +Infinity;
  let miny = +Infinity;
  let maxx = 0;
  let maxy = 0;

  pixels.forEach(function (px) {
    minx = Math.min(minx, px.x) >= 0 ? Math.min(minx, px.x) : 0;
    maxx = Math.max(maxx, px.x) <= gridSize - 1 ? Math.max(maxx, px.x) : gridSize - 1;
    miny = Math.min(miny, px.y) >= 0 ? Math.min(miny, px.y) : 0;
    maxy = Math.max(maxy, px.y) <= gridSize - 1 ? Math.max(maxy, px.y) : gridSize - 1;
  });

  return {
    minx: minx,
    maxx: maxx,
    miny: miny,
    maxy: maxy,
  };
};

export const getPixelBoundariesOfFrameXY = (pixels, width, height) => {
  let minx = +Infinity;
  let miny = +Infinity;
  let maxx = 0;
  let maxy = 0;

  pixels.forEach(function (px) {
    minx = Math.min(minx, px.x) >= 0 ? Math.min(minx, px.x) : 0;
    maxx = Math.max(maxx, px.x) <= width - 1 ? Math.max(maxx, px.x) : width - 1;
    miny = Math.min(miny, px.y) >= 0 ? Math.min(miny, px.y) : 0;
    maxy = Math.max(maxy, px.y) <= height - 1 ? Math.max(maxy, px.y) : height - 1;
  });

  return {
    minx: minx,
    maxx: maxx,
    miny: miny,
    maxy: maxy,
  };
};

const cropCanvas = (canvas, offsetX, offsetY, width, height) => {
  let buffer = document.createElement('canvas');
  const bufferContext = buffer.getContext('2d');
  buffer.width = width;
  buffer.height = height;
  bufferContext.drawImage(canvas, offsetX, offsetY, width, height, 0, 0, buffer.width, buffer.height);
  const data = buffer.toDataURL();
  buffer = null;
  return data;
};

const cropCanvasBlob = (canvas, offsetX, offsetY, width, height) => {
  return new Promise((resolve) => {
    let buffer = document.createElement('canvas');
    const bufferContext = buffer.getContext('2d');
    buffer.width = width;
    buffer.height = height;
    bufferContext.drawImage(canvas, offsetX, offsetY, width, height, 0, 0, buffer.width, buffer.height);
    buffer.toBlob((blob) => {
      buffer = null;
      resolve(blob);
    });
  });
};

const hex2rgb = (hex) => ({
  r: parseInt(hex.substr(1, 2), 16),
  g: parseInt(hex.substr(3, 2), 16),
  b: parseInt(hex.substr(5, 2), 16),
});

const dec2hex = (d) => (d < 16 ? '0' : '') + d.toString(16);
const rgb2hex = ({ r, g, b }) => dec2hex(r) + dec2hex(g) + dec2hex(b);

const getUnusedColor = (usedColors) => {
  usedColors = usedColors || [];
  // create check map
  const colorMap = {};
  usedColors.forEach((color) => {
    colorMap[color.toUpperCase().replace('#', '')] = true;
  });

  // start with white
  const color = {
    r: 255,
    g: 255,
    b: 0,
  };
  let match = null;
  let h = null;
  while (true) { //eslint-disable-line
    const hex = rgb2hex(color).toUpperCase();
    h = hex;
    if (!colorMap[hex]) {
      match = hex;
      break;
    } else {
      // pick a non null component to decrease its value
      const component = (color.r && 'r') || (color.g && 'g') || (color.b && 'b');
      if (component) {
        color[component] = color[component] - 1 //eslint-disable-line
      } else {
        // no component available, no match found
        match = 'FF00FF';
        break;
      }
    }
  }

  return { decimal: parseInt(match, 16), hex: h.toString(16), rgb: hex2rgb(`#${match}`) };
};

const getUsedColors = (layers) => {
  const colors = [];
  layers.forEach((layer) => {
    layer.pixelData.forEach((frame) => {
      // get colors
      frame.forEach((pixel) => {
        const color = pixel.shape ? pixel.shape.e : pixel.color;
        if (colors.findIndex((c) => c === color) === -1) {
          colors.push(color);
        }
      });
    });
  });

  return colors;
};

export const generateGIF = (cb) => (dispatch, getState) => {
  const { settings, data } = getState();
  const layers = data.layers;
  const transparencyColor = getUnusedColor(getUsedColors(layers)); // eslint-disable-line

  const exportAreaBounds = {
    xA: settings.xA || 0,
    xB: settings.xB || settings.gridSize,
    yA: settings.yA || 0,
    yB: settings.yB || settings.gridSize,
  };

  const exportArea = {
    ...exportAreaBounds,
    width: () => Math.abs(exportAreaBounds.xB - exportAreaBounds.xA),
    height: () => Math.abs(exportAreaBounds.yB - exportAreaBounds.yA),
  };

  const debugMultiplier = 2;
  const outputSize = debugMode ? settings.gridSize * debugMultiplier : settings.exportSize || settings.gridSize;
  const pixelSize = Math.ceil(outputSize / settings.gridSize);
  const outputSizeWidth = exportArea.width() * pixelSize;
  const outputSizeHeight = exportArea.height() * pixelSize;

  const numLayers = layers.length;
  const numFrames = layers[0].pixelData.length;
  const backgroundColor = settings.backgroundColor;

  let containsTransparentPixels = true;
  for (let i = 0; i < numFrames; i++) {
    const currentFrame = i;
    const layer = layers[0];
    let pixelDataCurrentFrame = layer.pixelData[currentFrame];

    if (layers.length > 1) {
      layers.forEach((l) => {
        pixelDataCurrentFrame.concat(l.pixelData[currentFrame]);
      });
    }
    const raw = normalizePixels(pixelDataCurrentFrame);
    /* eslint-disable */
    for (var gridX = 0; gridX < exportArea.width(); gridX++) {
      for (var gridY = 0; gridY < exportArea.height(); gridY++) {
        const check = { x: gridX, y: gridY };
        raw.forEach((px) => {
          const _px = px.x - exportArea.xA;
          const _py = px.y - exportArea.yA;
          // console.log('px py', _px, _py);
          containsTransparentPixels = true;
          if (_px === check.x && _py === check.y) {
            containsTransparentPixels = false;
          }
        });
      }
    }
    /* eslint-enable */
  }

  // console.log('found transparency', containsTransparentPixels);

  var gif = new GIF({
    workers: 8,
    quality: 0,
    width: outputSizeWidth,
    height: outputSizeHeight,
    workerScript: '/gif.worker.js',
    background: '#FFFFFF',
    transparent: backgroundColor === 'transparent' && containsTransparentPixels ? transparencyColor.hex : null,
  });

  const canvas = settings.spritesheetCanvasRef;
  const rawCanvas = settings.rawCanvasRef;
  const orgCanvasSize = canvas.width;

  canvas.width = outputSizeWidth;
  canvas.height = outputSizeHeight;
  rawCanvas.width = outputSizeWidth;
  rawCanvas.height = outputSizeHeight;

  const ctx = canvas.getContext('2d');

  for (let i = 0; i < numFrames; i++) {
    ctx.fillStyle = backgroundColor === 'transparent' ? 'rgba(0,0,0,0)' : backgroundColor;
    ctx.fillRect(0, 0, outputSizeWidth, outputSizeHeight);

    for (let x = 0; x < numLayers; x++) {
      const currentFrame = i;
      const layer = layers[x];
      const opacity = layer.opacity || 100;
      const alpha = opacity / 100;
      const layerOpacity = Math.round(alpha * 100) / 100;
      ctx.globalAlpha = layerOpacity;

      let pixelDataCurrentFrame = layer.pixelData[currentFrame];
      const rawPixelData = normalizePixels(pixelDataCurrentFrame);
      
      /* eslint-disable */
      rawPixelData.forEach((pixel) => {
        ctx.fillStyle = pixel.color === '#000000' && containsTransparentPixels ? '#080808' : pixel.color;
        const posX = pixel.x - exportArea.xA;
        const posY = pixel.y - exportArea.yA;
        const x = Math.ceil(posX * pixelSize);
        const y = Math.ceil(posY * pixelSize);
        ctx.fillRect(x, y, pixelSize, pixelSize);
      });
      /* eslint-enable */
    }

    var img = document.createElement('img');
    img.src = canvas.toDataURL('image/jpeg', 1.0);
    // document.body.appendChild(img);

    ctx.globalAlpha = 1;
    ctx.drawImage(img, 0, 0);
    gif.addFrame(ctx, { copy: true, delay: settings.fps });
    ctx.clearRect(0, 0, outputSizeWidth, outputSizeHeight);
  }

  gif.on('finished', function (blob, a) {
    // console.log('gif finished', blob, a); // eslint-disable-line
    gif.freeWorkers.forEach((w) => w.terminate());
    canvas.width = orgCanvasSize;
    canvas.height = orgCanvasSize;
    rawCanvas.width = orgCanvasSize;
    rawCanvas.height = orgCanvasSize;

    const b64encoded = window.btoa(
      new Uint8Array(a).reduce(function (data, byte) {
        return data + String.fromCharCode(byte);
      }, '')
    );

    if (!debugMode) {
      dispatch(requestSave());
      if (cb) {
        // pass data back to gen function
        cb(b64encoded);
      }
    } else {
      window.open(window.URL.createObjectURL(blob));
    }
  });
  gif.render();
};

export const getFlattenedFrameDataWithOffset = (frameIndex, crop) => (dispatch, getState) => {
  const { settings } = getState();
  const { layers } = getState().data;
  const backgroundColor = settings.backgroundColor;

  const exportAreaBounds = {
    xA: settings.xA || 0,
    xB: settings.xB || settings.gridSize,
    yA: settings.yA || 0,
    yB: settings.yB || settings.gridSize,
  };

  const exportArea = {
    ...exportAreaBounds,
    width: () => Math.abs(exportAreaBounds.xB - exportAreaBounds.xA),
    height: () => Math.abs(exportAreaBounds.yB - exportAreaBounds.yA),
  };

  // get boundaries
  const pixels = layers.reduce((total, { pixelData }) => total.concat(pixelData), []);

  const rawPixelData = normalizePixels(pixels);
  if (rawPixelData.length > 0) {
    const debugMultiplier = 2;
    const outputSize = debugMode ? settings.gridSize * debugMultiplier : settings.exportSize || settings.gridSize;

    const pixelSize = Math.ceil(outputSize / settings.gridSize);
    const outputSizeWidth = exportArea.width() * pixelSize;
    const outputSizeHeight = exportArea.height() * pixelSize;

    const boundaries = crop ? getPixelBoundariesOfFrame(rawPixelData, settings.gridSize) : getPixelBoundariesOfFrameXY(rawPixelData, exportArea.width(), exportArea.height());

    const canvas = settings.spritesheetCanvasRef;
    const minX = boundaries.minx;
    const minY = boundaries.miny;
    const maxX = boundaries.maxx;
    const maxY = boundaries.maxy;

    let frameWidth, frameHeight;
    if (crop) {
      frameWidth = (maxX + 1 - minX) * pixelSize;
      frameHeight = (maxY + 1 - minY) * pixelSize;
    } else {
      frameWidth = outputSizeWidth;
      frameHeight = outputSizeHeight;
    }

    canvas.width = crop ? outputSize : outputSizeWidth;
    canvas.height = crop ? outputSize : outputSizeHeight;

    const ctx = canvas.getContext('2d');
    const rectW = crop ? outputSize : outputSizeWidth;
    const rectH = crop ? outputSize : outputSizeHeight;
    ctx.clearRect(0, 0, rectW, rectH);
    
    rawPixelData.forEach((pixel) => {
      ctx.fillStyle = pixel.color;
      const posX = crop ? pixel.x - boundaries.minx : pixel.x;
      const posY = crop ? pixel.y - boundaries.miny : pixel.y;
      ctx.fillRect(posX * pixelSize, posY * pixelSize, pixelSize, pixelSize);
    });

    const numLayers = layers.length;
    ctx.fillStyle = backgroundColor === 'transparent' ? 'rgba(0,0,0,0)' : backgroundColor;
    ctx.fillRect(0, 0, rectW, rectH);

    for (let x = 0; x < numLayers; x++) {
      const currentFrame = frameIndex;
      const layer = layers[x];
      const opacity = layer.opacity || 100;
      const alpha = opacity / 100;
      const layerOpacity = Math.round(alpha * 100) / 100;
      ctx.globalAlpha = layerOpacity;

      let pixelDataCurrentFrame = layer.pixelData[currentFrame];
      const rawPixelData = normalizePixels(pixelDataCurrentFrame);

      rawPixelData.forEach((pixel) => {
        ctx.fillStyle = pixel.color; // ctx.fillStyle = pixel.color === '#000000' ? '#080808' : pixel.color;
        const posX = crop ? pixel.x - boundaries.minx : pixel.x - exportArea.xA;
        const posY = crop ? pixel.y - boundaries.miny : pixel.y - exportArea.yA;
        const x = Math.ceil(posX * pixelSize);
        const y = Math.ceil(posY * pixelSize);
        ctx.fillRect(x, y, pixelSize, pixelSize);
      });
    }

    var img = document.createElement('img');
    img.src = canvas.toDataURL('image/jpeg', 1.0);
    ctx.globalAlpha = 1;
    ctx.drawImage(img, 0, 0);

    // clear draw canvas
    const drawCanvas = getState().settings.drawCanvasRef;
    const drCtx = drawCanvas.getContext('2d');
    drCtx.clearRect(0, 0, rectW, rectH);

    if (crop) {
      const width = frameWidth;
      const height = frameHeight;
      const croppedData = cropCanvas(canvas, 0, 0, width, height);

      ctx.clearRect(0, 0, outputSize, outputSize);
      return {
        data: croppedData,
        width: width,
        height: height,
        grid: settings.gridSize,
        offsetX: boundaries.minx,
        offsetY: boundaries.miny,
      };
    } else {
      const width = outputSizeWidth;
      const height = outputSizeHeight;
      const data = cropCanvas(canvas, 0, 0, width, height);

      ctx.clearRect(0, 0, outputSizeWidth, outputSizeHeight);
      return {
        data: data,
        width: width,
        height: height,
        grid: settings.gridSize,
        offsetX: 0,
        offsetY: 0,
      };
    }
  }
};

export const getFrameDataWithOffset = (frameData, crop, flattenLayers, frameIndex) => (dispatch, getState) => {
  const { settings } = getState();
  const { layers } = getState().data;
  let pixelsOfCurrentFrame = layers[0].pixelData[frameIndex];
  if (flattenLayers && layers.length > 1) {
    for (var i = 1; i < layers.length; i++) {
      const add = layers[i].pixelData[frameIndex];
      pixelsOfCurrentFrame = pixelsOfCurrentFrame.concat(add);
    }
  } else {
    pixelsOfCurrentFrame = frameData || [];
  }

  const rawPixelData = normalizePixels(pixelsOfCurrentFrame);
  const blur = 0;

  if (rawPixelData.length > 0) {
    const outputSize = settings.exportSize || settings.gridSize;
    const boundaries = getPixelBoundariesOfFrame(rawPixelData, settings.gridSize);

    const canvas = settings.spritesheetCanvasRef;
    const pixelSize = outputSize / settings.gridSize;
    const minX = boundaries.minx;
    const minY = boundaries.miny;
    const maxX = boundaries.maxx;
    const maxY = boundaries.maxy;

    let frameWidth, frameHeight;
    if (crop) {
      frameWidth = (maxX + 1 - minX) * pixelSize;
      frameHeight = (maxY + 1 - minY) * pixelSize;
    } else {
      frameWidth = outputSize;
      frameHeight = outputSize;
    }

    canvas.width = frameWidth;
    canvas.height = frameHeight;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, outputSize, outputSize);
    const w = pixelSize;
    const h = pixelSize;

    const drawCanvas = getState().settings.drawCanvasRef;
    const drCtx = drawCanvas.getContext('2d');
    drCtx.clearRect(0, 0, outputSize, outputSize);

    rawPixelData.forEach((pixel) => {
      ctx.fillStyle = pixel.color;
      const posX = crop ? pixel.x - boundaries.minx : pixel.x;
      const posY = crop ? pixel.y - boundaries.miny : pixel.y;
      ctx.fillRect(posX * w - blur, posY * w - blur, w + blur, h + blur);
    });

    if (crop) {
      const width = frameWidth;
      const height = frameHeight;
      const croppedData = cropCanvas(canvas, 0, 0, width, height);
      return {
        data: croppedData,
        width: width,
        height: height,
        grid: settings.gridSize,
        offsetX: boundaries.minx,
        offsetY: boundaries.miny,
      };
    } else {
      const width = outputSize;
      const height = outputSize;
      const data = cropCanvas(canvas, 0, 0, width, height);

      return {
        data: data,
        width: width,
        height: height,
        grid: settings.gridSize,
        offsetX: 0,
        offsetY: 0,
      };
    }
  }
};

const getChunkedArrayWithProperty = (source, prop, limit) =>
  source.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / limit);
    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = []; // start a new chunk
    }
    resultArray[chunkIndex].push(item[prop]);
    return resultArray;
  }, []);

export const renderSpriteSheetWithData = (frames, crop, animationName) => (dispatch, getState) => {
  return new Promise((resolve) => {
    const settings = getState().settings;
    const canvas = settings.spritesheetCanvasRef;
    const ctx = canvas.getContext('2d');
    const outputSize = settings.exportSize || settings.gridSize;
    const maxX = frames.length > 4 ? 4 : frames.length;

    const chunksWidth = getChunkedArrayWithProperty(frames, 'frameWidth', maxX);
    const chunksHeight = getChunkedArrayWithProperty(frames, 'frameHeight', maxX);

    const canvasWidth = chunksWidth.reduce((res, item) => {
      const rowVal = item.reduce((acc, currentItem) => acc + currentItem, 0);
      return rowVal > res ? rowVal : res;
    }, 0);
    const canvasHeight = chunksHeight.reduce((res, item) => (res += Math.max(...item)), 0);

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    const spritesheetJSON = {
      frames: {},
    };

    let posX = 0;
    let posY = 0;
    let xCount = 0;
    let maxFrameHeight = [];
    frames.forEach((frame, index) => {
      const image = new Image();
      image.onload = function () {
        ctx.drawImage(image, posX, posY);

        // const offRatio = outputSize / settings.gridSize;
        const spriteSource = {
          x: frame.offsetX,
          y: frame.offsetY,
          w: frame.frameWidth,
          h: frame.frameHeight,
        };

        const frameSource = {
          x: posX,
          y: posY,
          w: frame.frameWidth,
          h: frame.frameHeight,
        };

        spritesheetJSON.frames[`${animationName}_${index}`] = {
          frame: frameSource,
          rotated: false,
          trimmed: crop,
          spriteSourceSize: spriteSource,
          sourceSize: { w: outputSize, h: outputSize },
        };

        posX += frame.frameWidth;
        xCount += 1;
        maxFrameHeight.push(frame.frameHeight);

        if (xCount > maxX - 1) {
          // console.log('-> 5', xCount, maxX - 1)
          posX = 0;
          xCount = 0;
          posY += Math.max(...maxFrameHeight);
          maxFrameHeight = [];
        }
      };
      image.src = frame.data;
    });

    setTimeout(async () => {
      spritesheetJSON.meta = {
        app: 'https://app.spritelove.com',
        version: '1.0',
        image: `${animationName}.png`,
        format: 'RGBA8888',
        size: { w: canvasWidth, h: canvasHeight },
        scale: '1',
        fps: settings.fps,
      };

      const sheet = await cropCanvasBlob(canvas, 0, 0, canvasWidth, canvasHeight);

      resolve({ json: spritesheetJSON, data: sheet });
    }, 0);
  });
};

export const centerFrameContent = () => (dispatch, getState) => {
  const data = getState().data;
  // save current state
  const layersCopy = JSON.stringify(getState().data.layers);
  dispatch(saveCurrentPixelData(JSON.parse(layersCopy)));

  const gridSize = getState().settings.gridSize;
  const pixelsOfCurrentFrame = data.layers[data.currentLayer].pixelData[data.currentFrame] || [];
  const rawPixelData = normalizePixels(pixelsOfCurrentFrame);

  if (rawPixelData.length > 0) {
    const boundaries = getPixelBoundariesOfFrame(rawPixelData, gridSize);

    const bw = (boundaries.maxx - boundaries.minx + 1) / 2;
    const bh = (boundaries.maxy - boundaries.miny + 1) / 2;
    const fw = gridSize / 2;
    const fh = gridSize / 2;
    const dx = Math.floor(fw - bw - boundaries.minx);
    const dy = Math.floor(fh - bh - boundaries.miny);

    const translatedPixels = rawPixelData.map((px) => {
      return { ...px, x: px.x + dx, y: px.y + dy };
    });

    const reducedSet = [];
    reducePixels(translatedPixels, reducedSet);
    const newSet = reducedSet.sort((a, b) => a.x - b.x).sort((a, b) => a.y - b.y);
    dispatch(setPixels(newSet));
    dispatch(generateSpriteData());
  }
};

export const updateBgCanvas = () => (dispatch, getState) => {
  const settings = getState().settings;
  const theme = getState().theme;
  const size = settings.screen.width;
  const w = (size / settings.gridSize) * settings.zoom;
  const bgSize = size / w;
  const ctx = settings.backgroundCanvasRef.getContext('2d');
  ctx.clearRect(0, 0, size, size);
  const colorA = theme.themes[theme.current].gridBgA;
  const colorB = theme.themes[theme.current].gridBgB;
  if (settings.showGrid) {
    let light = true;
    for (let i = 0; i < bgSize; i++) {
      for (let x = 0; x < bgSize; x++) {
        ctx.fillStyle = light ? colorA : colorB;
        ctx.fillRect(Math.ceil(i * w), Math.ceil(x * w), Math.ceil(w), Math.ceil(w));
        light = !light;
      }
      light = !light;
    }
    dispatch({ type: COMMON_TYPES.UPDATE_BACKGROUND_CANVAS });
  }
};

export const activateDefineArea = () => (dispatch) => dispatch({type: UI_TYPES.ACTIVATE_DEFINE_AREA})
export const closeDefineArea = () => (dispatch) => dispatch({type: UI_TYPES.CLOSE_DEFINE_AREA})

export const clearArea = () => (dispatch) => {
  dispatch({ type: UI_TYPES.CLEAR_AREA_DEFINITION });
  dispatch(updateAreaCanvas());
};

export const activateXA = () => (dispatch, getState) => {
  dispatch({ type: UI_TYPES.ACTIVATE_AREA_X_A })

  if(!getState().settings.xA) {
    dispatch(updateTempAreaValue({x: 0, y: 0}))
  }
};
export const setXA = (val) => (dispatch) => {
  dispatch({ type: UI_TYPES.SET_X_A, payload: val });
  dispatch(updateAreaCanvas());
};

export const activateXB = () => (dispatch, getState) => {
  dispatch({ type: UI_TYPES.ACTIVATE_AREA_X_B })

  if(!getState().settings.xB) {
    if(getState().settings.xA) {
      dispatch(updateTempAreaValue({x: getState().settings.xA + 2, y: 0}))
    } else {
      dispatch(updateTempAreaValue({x: 2, y: 0}))
    }
  }
};
export const setXB = (val) => (dispatch, getState) => {
  if(!getState().settings.xA) {
    dispatch({ type: UI_TYPES.SET_X_B, payload: val });
  } else {
    const minX = getState().settings.xA;
    dispatch({ type: UI_TYPES.SET_X_B, payload: val <= minX ? minX + 2 : val });
  }

  dispatch(updateAreaCanvas());
};

export const activateYA = () => (dispatch, getState) => {
  dispatch({ type: UI_TYPES.ACTIVATE_AREA_Y_A })

  if(!getState().settings.yA) {
    dispatch(updateTempAreaValue({x: 0, y: 0}))
  }
};
export const setYA = (val) => (dispatch) => {
  dispatch({ type: UI_TYPES.SET_Y_A, payload: val });
  dispatch(updateAreaCanvas());
};

export const activateYB = () => (dispatch, getState) => {
  dispatch({ type: UI_TYPES.ACTIVATE_AREA_Y_B })

  if(!getState().settings.yB) {
    if(getState().settings.yA) {
      dispatch(updateTempAreaValue({x: 0, y: getState().settings.yA + 2}))
    } else {
      dispatch(updateTempAreaValue({x: 0, y: 2}))
    }
  }
};
export const setYB = (val) => (dispatch, getState) => {
  
  if(!getState().settings.yA) {
    dispatch({ type: UI_TYPES.SET_Y_B, payload: val < 2 ? 2 : val });
  } else {
    const minY = getState().settings.yA || 0;
    dispatch({ type: UI_TYPES.SET_Y_B, payload: val <= minY ? minY + 2 : val });
  }
  dispatch(updateAreaCanvas());
};

export const updateTempAreaValue = (val) => (dispatch) => {
  dispatch({ type: UI_TYPES.UPDATE_TEMP_AREA_VALUE, payload: val });
  dispatch(updateAreaCanvas());
};

export const setAreaValue = (val) => (dispatch, getState) => {
  const areaType = getState().settings.areaDefineActiveType;
  // console.log('set area value', val, 'type', areaType)

  switch (areaType) {
    case 'XA':
      dispatch(setXA(val.x));
      break;
    case 'XB':
      dispatch(setXB(val.x));
      break;
    case 'YA':
      dispatch(setYA(val.y));
      break;
    case 'YB':
      dispatch(setYB(val.y));
      break;
    default:
      break;
  }
};

export const updateAreaCanvas = () => (dispatch, getState) => {
  const settings = getState().settings;
  const { areaDefineActive, areaDefineActiveType, tempAreaXY } = settings; //xA, xB, yA, yB
  let { xA, xB, yA, yB, gridSize } = settings;
  const gutter = 2;
  // const xA = 10;
  // const xB = 35;
  // const yA = 20;
  // const yB = 30;
  if (areaDefineActive && tempAreaXY) {
    // console.log('updateAreaCanvas', areaDefineActiveType, tempAreaXY);
    switch (areaDefineActiveType) {
      case 'XA':
        const xA_max = xB ? xB - gutter : gridSize - gutter;
        xA = tempAreaXY.x > xA_max ? xA_max : tempAreaXY.x;
        break;
      case 'XB':
        const xB_min = xA ? xA + gutter : gutter;
        xB = tempAreaXY.x < xB_min ? xB_min : tempAreaXY.x;
        break;
      case 'YA':
        const yA_max = yB ? yB - gutter : gridSize - gutter;
        yA = tempAreaXY.y > yA_max ? yA_max : tempAreaXY.y;
        break;
      case 'YB':
        const yB_min = yA ? yA + gutter : gutter;
        yB = tempAreaXY.y < yB_min ? yB_min : tempAreaXY.y;
        break;
      default:
        break;
    }
  } 

  const size = settings.screen.width;
  const w = (size / settings.gridSize) * settings.zoom;
  const bgSize = size / w;
  const ctx = settings.areaCanvasRef.getContext('2d');
  ctx.clearRect(0, 0, size, size);
  const lineWidth = 1;
  const triangleSize = 4;

  if(xA && xB && yA && yB) {
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(0, yA * w, xA * w, (yB - yA) * w);
    ctx.fillRect(xB * w, yA * w, (settings.gridSize - xB) * w, (yB - yA) * w);
    ctx.fillRect(0, 0, settings.gridSize * w, yA * w);
    ctx.fillRect(0, yB * w, settings.gridSize * w, (settings.gridSize - yB) * w );
  } else {
    if(xA) {
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(0, 0, xA * w, settings.gridSize * w);
    }
    if(xB) {
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(xB * w, 0, (settings.gridSize - xB) * w, settings.gridSize * w);
    }
    if(yA) {
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(0, 0, settings.gridSize * w, yA * w);
    }
    if(yB) {
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(0, yB * w, settings.gridSize * w, (settings.gridSize - yB) * w );
    }
  }

  ctx.fillStyle = 'cyan';
  for (let i = 0; i < bgSize + 1; i++) {
    for (let x = 0; x < bgSize + 1; x++) {
      if (i === xA) {
        ctx.fillRect(Math.ceil(i * w), Math.ceil(x * w), lineWidth, Math.ceil(w));

        if (x === 0) {
          ctx.beginPath(); // note usage below
          ctx.moveTo(Math.ceil(i * w) - triangleSize, 0); // start at top left corner of canvas
          ctx.lineTo(Math.ceil(i * w) + triangleSize, 0); // go 200px to right (x), straight line from 0 to 0
          ctx.lineTo(Math.ceil(i * w), triangleSize); // go to horizontal 100 (x) and vertical 200 (y)
          ctx.fill(); // connect and fill
        }
      }

      if (x === yA) {
        ctx.fillRect(Math.ceil(i * w), Math.ceil(x * w), Math.ceil(w), lineWidth);

        if (i === 0) {
          ctx.beginPath(); // note usage below
          ctx.moveTo(0, Math.ceil(x * w) - triangleSize); // start at top left corner of canvas
          ctx.lineTo(0, Math.ceil(x * w) + triangleSize); // go 200px to right (x), straight line from 0 to 0
          ctx.lineTo(triangleSize, Math.ceil(x * w)); // go to horizontal 100 (x) and vertical 200 (y)
          ctx.fill(); // connect and fill
        }
      }

      if (i === xB) {
        if (xB === settings.gridSize) {
          ctx.fillRect(Math.ceil(i * w) - lineWidth, Math.ceil(x * w), lineWidth, Math.ceil(w));
        } else {
          if(Math.ceil(i * w) < xA * w) {
            ctx.fillStyle = 'red';
            ctx.fillRect(xA * w, Math.ceil(x * w), lineWidth, Math.ceil(w));
          } else {
            ctx.fillStyle = 'cyan';
            ctx.fillRect(Math.ceil(i * w), Math.ceil(x * w), lineWidth, Math.ceil(w));
          }
        }

        if (x === 0) {
          ctx.beginPath(); // note usage below
          ctx.moveTo(Math.ceil(i * w) - triangleSize, 0); // start at top left corner of canvas
          ctx.lineTo(Math.ceil(i * w) + triangleSize, 0); // go 200px to right (x), straight line from 0 to 0
          ctx.lineTo(Math.ceil(i * w), triangleSize); // go to horizontal 100 (x) and vertical 200 (y)
          ctx.fill(); // connect and fill
        }
      }

      if (x === yB) {
        if (yB === settings.gridSize) {
          ctx.fillRect(Math.ceil(i * w), Math.ceil(x * w) - lineWidth, Math.ceil(w), lineWidth);
        } else {
          if(Math.ceil(x * w) < yA * w) {
            ctx.fillStyle = 'red';
            ctx.fillRect(Math.ceil(i * w), yA * w, Math.ceil(w), lineWidth);
          }
          else {
            ctx.fillStyle = 'cyan';
            ctx.fillRect(Math.ceil(i * w), Math.ceil(x * w), Math.ceil(w), lineWidth);
          }
        }

        if (i === 0) {
          ctx.beginPath(); // note usage below
          ctx.moveTo(0, Math.ceil(x * w) - triangleSize); // start at top left corner of canvas
          ctx.lineTo(0, Math.ceil(x * w) + triangleSize); // go 200px to right (x), straight line from 0 to 0
          ctx.lineTo(triangleSize, Math.ceil(x * w)); // go to horizontal 100 (x) and vertical 200 (y)
          ctx.fill(); // connect and fill
        }
      }
    }
  }
};

export const updateDrawCanvas = () => (dispatch, getState) => {
  const settings = getState().settings;
  const data = getState().data;
  const canvas = settings.drawCanvasRef;
  const size = settings.screen.width;
  const w = (size / settings.gridSize) * settings.zoom;
  const h = (size / settings.gridSize) * settings.zoom;
  const ctx = canvas.getContext('2d');
  const camXOffset = settings.cameraX;
  const camYOffset = settings.cameraY;

  ctx.clearRect(0, 0, size, size);

  // Layer onion skinning (all layers except current)
  if (getState().settings.onionLayers) {
    onionSkinLayers(data, ctx, settings, camXOffset, camYOffset, w, h);
  }

  // Onion skinning (previous frame)
  if (getState().settings.onionFramesPrev) {
    onionSkinPreFrame(data, ctx, settings, camXOffset, camYOffset, w, h);
  }

  // Onion skinning (next frame)
  if (getState().settings.onionFramesNext) {
    onionSkinNextFrame(data, ctx, settings, camXOffset, camYOffset, w, h);
  }

  // Draw curent frame with normal alpha
  ctx.globalAlpha = 1;

  /* eslint-disable */
  /* prettier-ignore */
  const test = [{"x":5,"y":3,"color":"#8BC34A"},{"x":6,"y":3,"color":"#8BC34A"},{"x":7,"y":3,"color":"#8BC34A"},{"x":8,"y":3,"color":"#8BC34A"},{"x":9,"y":3,"color":"#8BC34A"},{"x":10,"y":3,"color":"#8BC34A"},{"x":11,"y":3,"color":"#8BC34A"},{"x":12,"y":3,"color":"#8BC34A"},{"x":13,"y":3,"color":"#8BC34A"},{"x":14,"y":3,"color":"#8BC34A"},{"x":15,"y":3,"color":"#8BC34A"},{"x":16,"y":3,"color":"#8BC34A"},{"x":17,"y":3,"color":"#8BC34A"},{"x":18,"y":3,"color":"#8BC34A"},{"x":19,"y":3,"color":"#8BC34A"},{"x":20,"y":3,"color":"#8BC34A"},{"x":21,"y":3,"color":"#8BC34A"},{"x":22,"y":3,"color":"#8BC34A"},{"x":23,"y":3,"color":"#8BC34A"},{"x":24,"y":3,"color":"#8BC34A"},{"x":25,"y":3,"color":"#8BC34A"},{"x":26,"y":3,"color":"#8BC34A"},{"x":26,"y":4,"color":"#8BC34A"},{"x":26,"y":5,"color":"#8BC34A"},{"x":26,"y":6,"color":"#8BC34A"},{"x":26,"y":7,"color":"#8BC34A"},{"x":26,"y":8,"color":"#8BC34A"},{"x":26,"y":9,"color":"#8BC34A"},{"x":26,"y":10,"color":"#8BC34A"},{"x":26,"y":11,"color":"#8BC34A"},{"x":26,"y":12,"color":"#8BC34A"},{"x":26,"y":13,"color":"#8BC34A"},{"x":26,"y":14,"color":"#8BC34A"},{"x":26,"y":15,"color":"#8BC34A"},{"x":26,"y":16,"color":"#8BC34A"},{"x":26,"y":17,"color":"#8BC34A"},{"x":26,"y":18,"color":"#8BC34A"},{"x":26,"y":19,"color":"#8BC34A"},{"x":26,"y":20,"color":"#8BC34A"},{"x":26,"y":21,"color":"#8BC34A"},{"x":26,"y":22,"color":"#8BC34A"},{"x":26,"y":23,"color":"#8BC34A"},{"x":26,"y":24,"color":"#8BC34A"},{"x":26,"y":25,"color":"#8BC34A"},{"x":26,"y":26,"color":"#8BC34A"},{"x":26,"y":27,"color":"#8BC34A"},{"x":26,"y":28,"color":"#8BC34A"},{"x":25,"y":28,"color":"#8BC34A"},{"x":25,"y":29,"color":"#8BC34A"},{"x":24,"y":29,"color":"#8BC34A"},{"x":23,"y":29,"color":"#8BC34A"},{"x":22,"y":29,"color":"#8BC34A"},{"x":21,"y":29,"color":"#8BC34A"},{"x":20,"y":29,"color":"#8BC34A"},{"x":19,"y":29,"color":"#8BC34A"},{"x":18,"y":29,"color":"#8BC34A"},{"x":17,"y":29,"color":"#8BC34A"},{"x":16,"y":29,"color":"#8BC34A"},{"x":15,"y":29,"color":"#8BC34A"},{"x":14,"y":29,"color":"#8BC34A"},{"x":13,"y":29,"color":"#8BC34A"},{"x":12,"y":29,"color":"#8BC34A"},{"x":11,"y":29,"color":"#8BC34A"},{"x":10,"y":29,"color":"#8BC34A"},{"x":9,"y":29,"color":"#8BC34A"},{"x":9,"y":28,"color":"#8BC34A"},{"x":8,"y":28,"color":"#8BC34A"},{"x":7,"y":28,"color":"#8BC34A"},{"x":6,"y":28,"color":"#8BC34A"},{"x":5,"y":28,"color":"#8BC34A"},{"x":4,"y":28,"color":"#8BC34A"},{"x":4,"y":27,"color":"#8BC34A"},{"x":4,"y":26,"color":"#8BC34A"},{"x":4,"y":25,"color":"#8BC34A"},{"x":4,"y":24,"color":"#8BC34A"},{"x":4,"y":23,"color":"#8BC34A"},{"x":4,"y":22,"color":"#8BC34A"},{"x":4,"y":21,"color":"#8BC34A"},{"x":4,"y":20,"color":"#8BC34A"},{"x":4,"y":19,"color":"#8BC34A"},{"x":4,"y":18,"color":"#8BC34A"},{"x":4,"y":17,"color":"#8BC34A"},{"x":4,"y":16,"color":"#8BC34A"},{"x":5,"y":16,"color":"#8BC34A"},{"x":5,"y":15,"color":"#8BC34A"},{"x":5,"y":14,"color":"#8BC34A"},{"x":5,"y":13,"color":"#8BC34A"},{"x":5,"y":12,"color":"#8BC34A"},{"x":5,"y":11,"color":"#8BC34A"},{"x":5,"y":10,"color":"#8BC34A"},{"x":5,"y":9,"color":"#8BC34A"},{"x":5,"y":8,"color":"#8BC34A"},{"x":5,"y":7,"color":"#8BC34A"},{"x":5,"y":6,"color":"#8BC34A"},{"x":5,"y":5,"color":"#8BC34A"},{"x":5,"y":4,"color":"#8BC34A"},{"x":13,"y":9,"color":"#8BC34A"},{"x":13,"y":8,"color":"#8BC34A"},{"x":14,"y":8,"color":"#8BC34A"},{"x":15,"y":8,"color":"#8BC34A"},{"x":16,"y":8,"color":"#8BC34A"},{"x":17,"y":8,"color":"#8BC34A"},{"x":18,"y":8,"color":"#8BC34A"},{"x":18,"y":9,"color":"#8BC34A"},{"x":18,"y":10,"color":"#8BC34A"},{"x":18,"y":11,"color":"#8BC34A"},{"x":18,"y":12,"color":"#8BC34A"},{"x":18,"y":13,"color":"#8BC34A"},{"x":18,"y":14,"color":"#8BC34A"},{"x":18,"y":15,"color":"#8BC34A"},{"x":17,"y":16,"color":"#8BC34A"},{"x":17,"y":17,"color":"#8BC34A"},{"x":17,"y":18,"color":"#8BC34A"},{"x":17,"y":19,"color":"#8BC34A"},{"x":16,"y":19,"color":"#8BC34A"},{"x":16,"y":18,"color":"#8BC34A"},{"x":15,"y":18,"color":"#8BC34A"},{"x":14,"y":18,"color":"#8BC34A"},{"x":14,"y":17,"color":"#8BC34A"},{"x":13,"y":17,"color":"#8BC34A"},{"x":13,"y":16,"color":"#8BC34A"},{"x":13,"y":15,"color":"#8BC34A"},{"x":13,"y":14,"color":"#8BC34A"},{"x":13,"y":13,"color":"#8BC34A"},{"x":13,"y":12,"color":"#8BC34A"},{"x":13,"y":11,"color":"#8BC34A"},{"x":13,"y":10,"color":"#8BC34A"},{"x":13,"y":21,"color":"#8BC34A"},{"x":14,"y":21,"color":"#8BC34A"},{"x":14,"y":22,"color":"#8BC34A"},{"x":15,"y":22,"color":"#8BC34A"},{"x":16,"y":22,"color":"#8BC34A"},{"x":13,"y":22,"color":"#8BC34A"},{"x":13,"y":23,"color":"#8BC34A"},{"x":13,"y":24,"color":"#8BC34A"},{"x":13,"y":25,"color":"#8BC34A"},{"x":14,"y":26,"color":"#8BC34A"},{"x":15,"y":26,"color":"#8BC34A"},{"x":16,"y":26,"color":"#8BC34A"},{"x":17,"y":25,"color":"#8BC34A"},{"x":17,"y":24,"color":"#8BC34A"},{"x":17,"y":23,"color":"#8BC34A"},{"x":17,"y":22,"color":"#8BC34A"}]

  const blur = 0

  if (data.layers[data.currentLayer] && data.layers[data.currentLayer].pixelData[data.currentFrame]) {
    data.layers[data.currentLayer].pixelData[data.currentFrame].forEach((pixel) => {

      ctx.globalAlpha = data.layers[data.currentLayer].opacity / 100 || 1;
      // shape
      if (pixel.shape) {
        ctx.fillStyle = pixel.shape.e; // `#${((Math.random() * 0xffffff) << 0).toString(16)}`
        const posXs = pixel.shape.a - camXOffset;
        const posXe = pixel.shape.b + 1 - camXOffset;
        const posYs = pixel.shape.c - camYOffset;
        const posYe = pixel.shape.d + 1 - camYOffset;

        ctx.fillRect(
          Math.ceil(posXs * w - blur),
          Math.ceil(posYs * w - blur),
          Math.ceil((posXe - posXs) * w + blur),
          Math.ceil((posYe - posYs) * w + blur)
        );
      } else {
        // single pixel
        ctx.fillStyle = pixel.color;
        const posX = pixel.x - camXOffset;
        const posY = pixel.y - camYOffset;
        ctx.fillRect(Math.ceil(posX * w - blur), Math.ceil(posY * w - blur), Math.ceil(w + blur), Math.ceil(h + blur));
      }
    });
  }

  const testReducedPixels = false;
  const colorReducedChunks = true;
  
  if (testReducedPixels) {
    /* eslint-enable */
    const arr = data.layers[0].pixelData[0]; // test;
    // console.log('arr', arr);

    const reduced = [];

    reducePixels(
      arr.sort((a, b) => a.x - b.x).sort((a, b) => a.y - b.y),
      reduced
    );

    // console.log(arr.length, '<->', reduced.length)
    // const decreaseValue = arr.length - reduced.length;
    // const percentage = (decreaseValue / arr.length) * 100;
    // console.log(`Reduce percentage: ${percentage.toFixed(2)}%`)

    reduced.forEach((sprite) => {
      if (sprite.shape) {
        // console.log('shape', sprite.shape)
        // multiple sprite.shape.e //
        ctx.fillStyle = colorReducedChunks ? `#${((Math.random() * 0xffffff) << 0).toString(16)}` : sprite.shape.e;
        const posXs = sprite.shape.a - camXOffset;
        const posXe = sprite.shape.b + 1 - camXOffset;
        const posYs = sprite.shape.c - camYOffset;
        const posYe = sprite.shape.d + 1 - camYOffset;

        ctx.fillRect(posXs * w - blur, posYs * w - blur, (posXe - posXs) * w + blur, (posYe - posYs) * w + blur);
      } else {
        // console.log('single pixel')
        ctx.fillStyle = sprite.color; // '#000000'
        const posX = sprite.x - camXOffset;
        const posY = sprite.y - camYOffset;
        ctx.fillRect(posX * w - blur, posY * w - blur, w + blur, h + blur);
      }
      ctx.fillStyle = '#FFFFFF';
    });
  }

  dispatch({ type: COMMON_TYPES.DRAW_CANVAS_UPDATE });
};

// RAW CANVAS (ALL LAYERS)
export const updateRAWCanvas = (layer = null, frame = null) => (dispatch, getState) => { // eslint-disable-line
  const settings = getState().settings;
  const data = getState().data;
  const canvas = settings.rawCanvasRef;
  const size = getState().settings.screen.width;
  const blur = 0;
  // Use frame from args when loading data
  const currentFrame = frame || data.currentFrame;

  const w = size / settings.gridSize;
  const h = size / settings.gridSize;

  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, size, size);

  data.layers.forEach((layer, index) => {
    const layerOpacity = data.layers[index].opacity / 100 || 1;
    ctx.globalAlpha = layerOpacity;

    if (layer.pixelData[currentFrame]) {
      layer.pixelData[currentFrame].forEach((pixel) => {
        // shape
        if (pixel.shape) {
          ctx.fillStyle = pixel.shape.e; // `#${((Math.random() * 0xffffff) << 0).toString(16)}`
          const posXs = pixel.shape.a;
          const posXe = pixel.shape.b + 1;
          const posYs = pixel.shape.c;
          const posYe = pixel.shape.d + 1;

          ctx.fillRect(
            Math.ceil(posXs * w - blur),
            Math.ceil(posYs * w - blur),
            Math.ceil((posXe - posXs) * w + blur),
            Math.ceil((posYe - posYs) * w + blur)
          );
        } else {
          // single pixel
          ctx.fillStyle = pixel.color;
          const posX = pixel.x;
          const posY = pixel.y;
          ctx.fillRect(
            Math.ceil(posX * w - blur),
            Math.ceil(posY * w - blur),
            Math.ceil(w + blur),
            Math.ceil(h + blur)
          );
        }
      });
    }
  });

  dispatch({ type: COMMON_TYPES.RAW_CANVAS_UPDATE });
};

// RAW FRAME CANVAS
export const updateRAWFrameCanvas = (layer = null, frame = null) => (dispatch, getState) => {
  const settings = getState().settings;
  const data = getState().data;
  const canvas = settings.rawFrameRef;
  const size = getState().settings.screen.width;
  const blur = 0;

  // Use layer / frame from args when loading data
  const currentLayer = layer || data.currentLayer;
  const currentFrame = frame || data.currentFrame;

  const w = size / settings.gridSize;
  const h = size / settings.gridSize;

  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, size, size);

  if (data.layers[currentLayer] && data.layers[currentLayer].pixelData) {
    if (data.layers[currentLayer].pixelData[currentFrame]) {
      ctx.globalAlpha = data.layers[data.currentLayer].opacity / 100 || 1;

      data.layers[currentLayer].pixelData[currentFrame].forEach((pixel) => {
        // shape
        if (pixel.shape) {
          ctx.fillStyle = pixel.shape.e; // `#${((Math.random() * 0xffffff) << 0).toString(16)}`
          const posXs = pixel.shape.a;
          const posXe = pixel.shape.b + 1;
          const posYs = pixel.shape.c;
          const posYe = pixel.shape.d + 1;

          ctx.fillRect(
            Math.ceil(posXs * w - blur),
            Math.ceil(posYs * w - blur),
            Math.ceil((posXe - posXs) * w + blur),
            Math.ceil((posYe - posYs) * w + blur)
          );
        } else {
          // single pixel
          ctx.fillStyle = pixel.color;
          const posX = pixel.x;
          const posY = pixel.y;
          ctx.fillRect(
            Math.ceil(posX * w - blur),
            Math.ceil(posY * w - blur),
            Math.ceil(w + blur),
            Math.ceil(h + blur)
          );
        }
      });
    }
  }

  dispatch({ type: COMMON_TYPES.RAW_FRAME_CANVAS_UPDATE });
};
